// Lazy, single Supabase client for the whole app. The SDK is dynamically
// imported only when the project is configured, so an unconfigured build (no env
// keys — e.g. CI, the smoke test, anyone who hasn't set up an account backend)
// never loads supabase-js at all and behaves exactly as the local-only app did.

import type { SupabaseClient } from "@supabase/supabase-js";

const URL = import.meta.env.VITE_SUPABASE_URL;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;

// True only when both keys are present — the single gate every auth/sync entry
// point checks before doing anything.
export function isSupabaseConfigured(): boolean {
  return !!URL && !!ANON;
}

let _client: SupabaseClient | null = null;
let _pending: Promise<SupabaseClient> | null = null;

// Resolve the shared client, creating it (and loading the SDK) on first use.
// Throws if called while unconfigured — callers must gate on isSupabaseConfigured.
export async function getSupabaseClient(): Promise<SupabaseClient> {
  if (_client) return _client;
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase is not configured (missing VITE_SUPABASE_URL / _ANON_KEY).");
  }
  if (!_pending) {
    _pending = import("@supabase/supabase-js").then(({ createClient }) => {
      // persistSession + autoRefreshToken keep the user signed in across reloads
      // (token in localStorage) so XR can read already-synced data without a
      // mid-session login.
      _client = createClient(URL as string, ANON as string, {
        auth: { persistSession: true, autoRefreshToken: true },
      });
      return _client;
    });
  }
  return _pending;
}
